import produce from "immer";
import React, { FC } from "react";
import { useDrop } from "react-dnd";
import { useDispatch, useSelector } from "react-redux";
import { startRemoveByDustbin } from "../../actions/dustbinAction";
import { startRemoveElement } from "../../actions/elementListAction";
import { startEditSession } from "../../actions/sessionListAction";
import { Element } from "../../models/elementModel";
import { Row } from "../../models/rowModel";
import { Section } from "../../models/sectionModel";
import { TabModel } from "../../models/tabModel";
import { AppState } from "../../reducers/rootReducer";
import { getAllRows } from "../../selectors/rowListSelector";
import { ItemTypes } from "../../utils/itemTypes";
import "./deleteAreaComponent.scss";

const DeleteAreaComponent: FC = () => {
  const dispatch = useDispatch();
  const sessionState = useSelector((state: AppState) => state.session);
  const rowState = useSelector(getAllRows);
  const sectionState = useSelector((state: AppState) => state.sectionList);
  const elementState = useSelector((state: AppState) => state.elementList);
  const tabState = useSelector((state: AppState) => state.tabList);

  const removeAction = (
    rows: Row[],
    sections: Section[],
    elements: Element[],
    tabs: TabModel[]
  ) => {
    dispatch(startRemoveByDustbin(rows, sections, elements, tabs));
  };

  const findTabDependents = (tabs: TabModel[]) => {
    let rowList: Row[] = [];
    let sectionList: Section[] = [];
    let elementList: Element[] = [];

    for (let i = 0; i < tabs.length; i++) {
      const tempRowListByTabId: Row[] = rowState.filter((row) => {
        return row.tabId === tabs[i].id;
      });
      rowList = rowList.concat(tempRowListByTabId);
    }

    for (let j = 0; j < rowList.length; j++) {
      const tempSectionListByRowId: Section[] = sectionState.filter(
        (section) => {
          return section.rowId === rowList[j].id;
        }
      );
      sectionList = sectionList.concat(tempSectionListByRowId);
    }

    for (let k = 0; k < sectionList.length; k++) {
      const tempElementListToRemoveBySectionId: Element[] = elementState.present.filter(
        (element) => {
          return element.sectionId === sectionList[k].id;
        }
      );
      elementList = elementList.concat(tempElementListToRemoveBySectionId);
    }

    return {
      rowList: rowList,
      sectionList: sectionList,
      elementList: elementList,
    };
  };

  const findTabListByElement = (element: Element) => {
    let tabList: TabModel[] = tabState.filter((tab) => {
      return tab.elementId === element.id;
    });

    let rowListByTabElement: Row[] = [];
    let sectionListByTabElement: Section[] = [];
    let elementListByTabElement: Element[] = [];

    for (let i = 0; i < tabList.length; i++) {
      const tempRowListByTabId: Row[] = rowState.filter((row) => { if(row.type === 'Tab' && row.tabId === tabList[i].id) return row});
      rowListByTabElement = rowListByTabElement.concat(tempRowListByTabId);
    }

    for (let j = 0; j < rowListByTabElement.length; j++) {
      const tempSectionListByRowId: Section[] = sectionState.filter(
        (section) => {
          return section.rowId === rowListByTabElement[j].id;
        }
      );
      sectionListByTabElement = sectionListByTabElement.concat(
        tempSectionListByRowId
      );
    }

    for (let k = 0; k < sectionListByTabElement.length; k++) {
      const tempElementListToRemoveBySectionId: Element[] = elementState.present.filter(
        (element) => {
          return element.sectionId === sectionListByTabElement[k].id;
        }
      );
      elementListByTabElement = elementListByTabElement.concat(
        tempElementListToRemoveBySectionId
      );
    }

    for (let index = 0; index < elementListByTabElement.length; index++) {
      if (elementListByTabElement[index].type === "tab") {
        tabList = tabList.concat(
          findTabListByElement(elementListByTabElement[index])
        );
      }
    }

    return tabList;
  };

  const findTabListByElementList = (elements: Element[]) => {
    let tabList: TabModel[] = [];
    for (let index = 0; index < elements.length; index++) {
      tabList = tabList.concat(findTabListByElement(elements[index]));
    }
    return tabList;
  };

  const [{ isOver }, drop] = useDrop({
    accept: [
      ItemTypes.ROW_MOVE,
      ItemTypes.SECTION_MOVE,
      ItemTypes.ELEMENT_MOVE,
    ],
    drop(item: any, monitor) {
      const didDrop = monitor.didDrop();
      const dropType = monitor.getItemType();
      if (didDrop) {
        return;
      }
      const newSession = produce(sessionState, (draft) => {
        draft.selectedType = "";
      });
      if (dropType === ItemTypes.ROW_MOVE) {
        let sectionListToRemove = sectionState.filter((section) => {
          return section.rowId === item.dragItem.id;
        });
        let elementListToRemove: Element[] = [];
        for (let index = 0; index < sectionListToRemove.length; index++) {
          const elementListBySectionId: Element[] = elementState.present.filter(
            (element) => {
              return element.sectionId === sectionListToRemove[index].id;
            }
          );
          elementListToRemove = elementListToRemove.concat(
            elementListBySectionId
          );
        }
        let tabListToRemove: TabModel[] =
          findTabListByElementList(elementListToRemove);
        let tabDependents = findTabDependents(tabListToRemove);
        let rowListToRemove: Row[] = tabDependents.rowList;
        sectionListToRemove = sectionListToRemove.concat(
          tabDependents.sectionList
        );
        elementListToRemove = elementListToRemove.concat(
          tabDependents.elementList
        );
        rowListToRemove.push(item.dragItem);
        dispatch(startEditSession(newSession));
        removeAction(
          rowListToRemove,
          sectionListToRemove,
          elementListToRemove,
          tabListToRemove
        );
      } else if (dropType === ItemTypes.SECTION_MOVE) {
        dispatch(startEditSession(newSession));
        let elementListToRemove = elementState.present.filter((element) => {
          return element.sectionId === item.dragItem.id;
        });
        let tabListToRemove: TabModel[] =
          findTabListByElementList(elementListToRemove);
        let tabDependents = findTabDependents(tabListToRemove);
        let rowListToRemove: Row[] = tabDependents.rowList;
        let sectionListToRemove: Section[] = tabDependents.sectionList;
        elementListToRemove = elementListToRemove.concat(
          tabDependents.elementList
        );
        sectionListToRemove.push(item.dragItem);
        removeAction(
          rowListToRemove,
          sectionListToRemove,
          elementListToRemove,
          tabListToRemove
        );
      } else if (dropType === ItemTypes.ELEMENT_MOVE) {
        console.log(item);
        dispatch(startEditSession(newSession));
        let tabListToRemove: TabModel[] = findTabListByElement(item.dragItem);
        let tabDependents = findTabDependents(tabListToRemove);
        let rowListToRemove: Row[] = tabDependents.rowList;
        let sectionListToRemove: Section[] = tabDependents.sectionList;
        let elementListToRemove: Element[] = tabDependents.elementList;
        elementListToRemove.push(item.dragItem);
        removeAction(
          rowListToRemove,
          sectionListToRemove,
          elementListToRemove,
          tabListToRemove
        );
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  return (
    <div className="container-fluid bg-light dustbin-panel">
      <div className="row dustbin-area">
        {/* <div className="col"></div> */}
        <div ref={drop} className="col p-3 dustbin">
          <i className="fa fa-trash fa-3x"></i>
        </div>
        {/* <div className="col"></div> */}
      </div>
    </div>
  );
};

export default DeleteAreaComponent;
